import { createConnectPopup } from "./createConnectPopup";

const MONOLITH_BASE_URL = process.env.NEXT_PUBLIC_ZAPIER_ORIGIN;
export const OAUTH_ACCOUNT_ID_PARAM = "_zapier_account_id";
export const OAUTH_SHARED_PARAM = "_zapier_is_shared";
export const AUTH_ID_PARAM = "_zapier_auth_id";
export const AUTH_DESTINATION_SELECTED_API_PARAM =
  "_zapier_destination_selected_api";
export const AUTH_EXPECTED_IDENTIFIER_PARAM = "_zapier_expected_identifier";
const AUTH_FOR_SIGNUP_PARAM = "for_signup";

// Shared param for non-OAuth auths
export const SHARED_AUTH_PARAM = "isShared";

// Event type that should be emitted (postMessage) from the auth window
const AUTH_SUCCESS_EVENT_TYPE = "authenticationSuccess";

// Auth specific errors
export const AUTH_FAILED_CONN = "AUTH_FAILED_CONN";

const newAuthError = (code: string, message: string) => {
  const err = new Error(message);
  (err as any).code = code;
  return err as Error;
};

export const addParamsToUrl = (url: string, params: Record<string, any>) => {
  const urlObj = new URL(url, MONOLITH_BASE_URL);
  Object.keys(params).forEach((key) => {
    //only append the params with truthy values, or "false", null and undefined will not be appended, and don't append duplicate params
    if (
      params[key] !== undefined &&
      params[key] !== null &&
      !urlObj.searchParams.has(key)
    ) {
      urlObj.searchParams.append(key, params[key]);
    }
  });
  return urlObj.toString();
};

interface ConnectUrlParams {
  serviceKey?: string;
  accountId?: number;
  isShared?: boolean;
  authId?: string;
  authIdentifier?: string;
  destinationServiceKey?: string;
  isForSignup?: boolean;
}

export const getConnectUrl = ({
  serviceKey,
  accountId,
  isShared,
  authId,
  authIdentifier,
  destinationServiceKey,
  isForSignup,
}: ConnectUrlParams) => {
  return addParamsToUrl(`/engine/auth/start/${serviceKey}/`, {
    [OAUTH_ACCOUNT_ID_PARAM]: accountId,
    [OAUTH_SHARED_PARAM]: isShared,
    [AUTH_ID_PARAM]: authId,
    [AUTH_EXPECTED_IDENTIFIER_PARAM]: authIdentifier,
    [AUTH_DESTINATION_SELECTED_API_PARAM]: destinationServiceKey,
    [AUTH_FOR_SIGNUP_PARAM]: isForSignup,
  });
};

// Message creator to post from authentication window on successful connection.
export const createAuthConnectionMessage = (authId?: string) => ({
  type: AUTH_SUCCESS_EVENT_TYPE,
  authId,
});

// Takes `win` popup window that authorizes a connection.
// Returns a `Promise` that will `resolve` if an
// `authId` is provided by `win` or `reject` if no
// `authId` is provided. `win` provides `authId` via
// a `postMessage` event to our `window`. We `poll`
// until `win` is `closed`. Would be nice to make that
// event based using `unload` or something, but not
// having a lot of luck with that.
const waitForConnection = (win: Window, isOauth?: boolean) => {
  return new Promise((resolve, reject) => {
    const origin = MONOLITH_BASE_URL;
    let authId: string | undefined;
    let success = false;

    const onMessage = (e: MessageEvent) => {
      if (e.origin === origin && e.data.type === AUTH_SUCCESS_EVENT_TYPE) {
        authId = e.data.authId;
        success = true;
      }
    };
    window.addEventListener("message", onMessage);

    const poll = () => {
      if (win.closed) {
        // If the parent window loses focus, then we'll detect the window is
        // closed before we receive our message. Wait for the next event turn
        // to make sure all messages are flushed.
        setTimeout(() => {
          window.removeEventListener("message", onMessage);

          // oauth will not always return auth id
          if (!success || (authId == null && !isOauth)) {
            reject(
              newAuthError(AUTH_FAILED_CONN, "Failed to connect account."),
            );
          }

          resolve(authId);
        }, 0);
      } else {
        setTimeout(poll, 1000);
      }
    };
    poll();
  });
};

export const safelyAddSharedAuthParamsToUrl = (
  url: string,
  params: ConnectUrlParams,
  isOauth?: boolean,
  hasUpfrontFields?: boolean,
) => {
  // It's only safe to add params to the URL if the auth URL is internal.
  // Tacking on params to an external url like an OAuth authorize URL might get
  // rejected by the partner app.
  // We still need to add the params if there are upfront fields, or the auth
  // won't initialize properly.
  const isSafeToAddParamsToUrl = !isOauth || hasUpfrontFields;
  return !isSafeToAddParamsToUrl
    ? url
    : addParamsToUrl(url, {
        [SHARED_AUTH_PARAM]: params.isShared,
        [AUTH_ID_PARAM]: params.authId,
      });
};

/**
 * This function will open a new connect auth window. If it is possible, this
 * function will first open a splash (loading) screen and then redirect to the
 * desired auth configuration, or it will immediately open the auth config.
 */

interface ConnectToAuthParams {
  accountId: number | undefined;
  authId?: string;
  authIdentifier?: string;
  destinationServiceKey?: string;
  hasUpfrontFields?: boolean;
  isForSignup?: boolean;
  isOauth: boolean;
  isShared?: boolean;
  onError?: (e?: any) => void;
  onTrack?: (event: string, opts: Record<string, unknown>) => Promise<any>;
  serviceKey?: string;
}

export const connectToAuth = async ({
  accountId,
  hasUpfrontFields,
  isShared,
  isOauth,
  serviceKey,
  authId,
  authIdentifier, // Require created auth to have this identity.
  destinationServiceKey,
  onTrack = () => Promise.resolve(),
  isForSignup,
}: ConnectToAuthParams) => {
  const params = {
    accountId,
    isShared,
    serviceKey,
    authId,
    authIdentifier,
    destinationServiceKey,
    isForSignup,
  };

  // Create the splash popup if possible, then retrieve the connect URL
  // to ensure it is valid. We do these as separate steps because we want the
  // popup window to open ASAP rather than waiting for the `fetchConnectUrl` to
  // resolve.

  const url = await getConnectUrl(params);
  const parameterizedUrl = safelyAddSharedAuthParamsToUrl(
    url,
    params,
    isOauth,
    hasUpfrontFields,
  );

  const win = createConnectPopup(parameterizedUrl);

  onTrack("auth: redirect to first leg", {
    accountId,
    authId,
    isForSignup,
    isOauth,
    parameterizedUrl,
    serviceKey,
  });

  try {
    const conn = await waitForConnection(win, isOauth);

    onTrack("auth: connection success", {
      accountId,
      authId,
      conn,
      isForSignup,
      isOauth,
      parameterizedUrl,
      serviceKey,
    });

    return conn;
  } catch (e: any) {
    onTrack("auth: could not get connection", {
      accountId,
      authId,
      error: e.message,
      isForSignup,
      isOauth,
      parameterizedUrl,
      serviceKey,
    });

    throw e;
  }
};
